import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stack, Text, SimpleGrid, ThemeIcon, Group, Box, Skeleton,
  Alert, Button, Title,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { WidgetCard } from '@/components/ui'
import { notifications } from '@mantine/notifications'
import {
  IconAlertTriangle, IconBuilding, IconServer, IconShieldCheck, IconActivity,
  IconPlus, IconArrowRight,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { gitlabApi } from '@/api/gitlab'
import { whatsapiAdminApi } from '@/api/whatsapi'
import { awsOrgProvisioningApi } from '@/api/awsOrgProvisioning'
import { useAuthStore } from '@/store/auth'
import { GitLabConfigModal } from '@/components/modals/GitLabConfigModal'
import { AwsRegionsModal } from '@/components/admin/tenants/AwsRegionsModal'

import { TenantsToolbar } from '@/components/admin/tenants/TenantsToolbar'
import {
  TenantsFilterPanel,
  type TenantFilters,
  EMPTY_FILTERS,
  countActiveFilters,
} from '@/components/admin/tenants/TenantsFilterPanel'
import type { TenantRow } from '@/components/admin/tenants/TenantsTable'
import { TenantsOuTreeView } from '@/components/admin/tenants/TenantsOuTreeView'
import { ProvisioningErrorModal } from '@/components/admin/tenants/ProvisioningErrorModal'
import { OrgSyncButton } from '@/components/admin/tenants/OrgSyncButton'

/**
 * Predicado de busca textual de um tenant. Optional chaining nos tres campos
 * porque, apesar do schema dizer NOT NULL, em runtime uma linha pode chegar
 * com ou_id/name/environment null/undefined (ex.: cache persistido de shape
 * antigo). Sem a guarda, `.toLowerCase()` lancava TypeError e derrubava a
 * tela inteira (ScreenErrorBoundary) ao digitar. `q` deve vir ja em minusculo.
 */
export function tenantMatchesSearch(t: TenantRow, q: string): boolean {
  return (
    (t.name?.toLowerCase().includes(q) ?? false) ||
    (t.ou_id?.toLowerCase().includes(q) ?? false) ||
    (t.environment?.toLowerCase().includes(q) ?? false)
  )
}

/* ── KPI Card ── */
function KpiCard({ icon: Icon, color, value, label, sub }: {
  icon: typeof IconBuilding
  color: string
  value: string | number
  label: string
  sub?: string
}) {
  return (
    <WidgetCard accent={color === 'inovareTeal' ? 'primary' : 'none'} p="md">
      <Group gap="md">
        <ThemeIcon size={44} radius="md" variant="light" color={color}>
          <Icon size={22} />
        </ThemeIcon>
        <Box>
          <Text size="xl" fw={700} c="var(--ai-text-primary)" style={{ lineHeight: 1 }}>
            {value}
          </Text>
          <Text size="xs" c="var(--ai-text-muted)" mt={2}>{label}</Text>
          {sub && <Text size="xs" c="dimmed" mt={1}>{sub}</Text>}
        </Box>
      </Group>
    </WidgetCard>
  )
}

export function Tenants() {
  const navigate = useNavigate()
  const { user, selectedTenantId } = useAuthStore()
  const refreshUser = useAuthStore((s) => s.refreshUser)
  const qc = useQueryClient()

  /* ── State ── */
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<TenantFilters>(EMPTY_FILTERS)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [gitlabModalOpened, { open: openGitlabModal, close: closeGitlabModal }] = useDisclosure(false)
  const [gitlabModalTenantId, setGitlabModalTenantId] = useState<number>(0)
  // Modal de erro de provisionamento — implementacao completa em TASK-2339.
  // Por enquanto so registramos o tenant clicado para a futura wiring.
  const [provisioningErrorOpened, { open: openProvisioningErrorModal, close: closeProvisioningErrorModal }] = useDisclosure(false)
  const [provisioningErrorTenant, setProvisioningErrorTenant] = useState<TenantRow | null>(null)

  /* ── Query ── */
  const { data: tenants, isLoading, error } = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: async () => {
      const res = await api.get<{ items: TenantRow[] }>('/v1/admin/tenants')
      return res.data.items
    },
  })

  // include_legacy=false: OU que sumiu da AWS carrega lapide (`is_legacy`) e
  // esta tela responde "o que existe agora". Pedi-la de volta era o que fazia
  // uma OU apagada aparecer como ambiente vazio. Ver TASK-10146.
  const { data: ouTreeData, error: ouTreeError } = useQuery({
    queryKey: ['aws-ou-tree', false],
    queryFn: () => awsOrgProvisioningApi.getOuTree(false),
  })

  /* ── Mutations ── */
  // Set<number> de tenant_ids com mutation in-flight. Substitui o
  // numero unico anterior pra suportar mutations CONCORRENTES sem race
  // (clicar A, depois B antes de A completar). v1.27.2.
  const [fieldPendingIds, setFieldPendingIds] = useState<Set<number>>(
    () => new Set(),
  )

  // Optimistic update CIRURGICO: snapshot guarda APENAS (id, field, oldValue)
  // do tenant clicado — em onError, reverte SO esse campo. Antes guardava
  // o array inteiro e restaurava tudo, o que clobberava optimistic updates
  // concorrentes de outros toggles in-flight (causa raiz do bug "outros
  // toggles flipam ao desabilitar um tenant").
  const updateMutation = useMutation<
    void,
    Error,
    { id: number; field: string; value: boolean },
    { id: number; field: string; oldValue: unknown } | undefined
  >({
    mutationFn: async ({ id, field, value }) => {
      await api.patch(
        `/v1/admin/tenants/${id}`,
        { [field]: value },
      )
    },
    onMutate: async ({ id, field, value }) => {
      setFieldPendingIds((prev) => {
        const next = new Set(prev)
        next.add(id)
        return next
      })
      await qc.cancelQueries({ queryKey: ['admin-tenants'] })
      const current = qc.getQueryData<TenantRow[]>(['admin-tenants'])
      const oldValue = current?.find((t) => t.id === id)?.[
        field as keyof TenantRow
      ]
      qc.setQueryData<TenantRow[]>(['admin-tenants'], (old) =>
        old?.map((t) =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          t.id === id ? ({ ...t, [field]: value } as any) : t,
        ),
      )
      return { id, field, oldValue }
    },
    onSuccess: async (_data, variables) => {
      // Ativar/desativar um tenant muda a lista visivel no seletor da sidebar
      // (user.tenants vem de /auth/me). Refetcha a auth store pra refletir na hora.
      if (variables.field === 'is_active') {
        await refreshUser()
      }
      notifications.show({ title: 'Tenant atualizado', message: 'Alteração salva com sucesso', color: 'teal' })
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        // CIRURGICO: reverte SO o campo especifico do tenant especifico.
        // Outros tenants e outras mutations in-flight ficam intactos.
        qc.setQueryData<TenantRow[]>(['admin-tenants'], (current) =>
          current?.map((t) =>
            t.id === ctx.id
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ? ({ ...t, [ctx.field]: ctx.oldValue } as any)
              : t,
          ),
        )
      }
      notifications.show({ title: 'Erro', message: 'Falha ao atualizar tenant', color: 'red' })
    },
    onSettled: (_data, _err, vars) => {
      setFieldPendingIds((prev) => {
        const next = new Set(prev)
        next.delete(vars.id)
        return next
      })
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
      // O toggle de `has_soc` passa por aqui (campo generico). A aba "Times
      // de SOC" (useSocTeams, chave 'soc-teams') mostra `is_active` do time
      // derivado desta mesma coluna — sem invalidar, ela pode dizer "Ativo"
      // logo depois de o has_soc ter sido desligado aqui.
      if (vars.field === 'has_soc') {
        qc.invalidateQueries({ queryKey: ['soc-teams'] })
      }
    },
  })

  const zabbixToggleMutation = useMutation({
    mutationFn: async ({ tenantId, enabled }: { tenantId: number; enabled: boolean }) => {
      await api.put(`/v1/admin/tenants/${tenantId}/zabbix/config`, { enabled })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
      notifications.show({ title: 'Zabbix atualizado', message: 'Integração atualizada com sucesso', color: 'teal' })
    },
    onError: (err: any) => {
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
      const detail = err?.response?.data?.detail
      if (detail === 'group_not_found') {
        notifications.show({ title: 'Erro', message: 'Grupo não encontrado no Zabbix. Crie o grupo primeiro.', color: 'red' })
      } else {
        notifications.show({ title: 'Erro', message: 'Falha ao atualizar configuração Zabbix', color: 'red' })
      }
    },
  })

  // Set<number> de tenant_ids com WhatsApi toggle in-flight. Mesmo padrao
  // do fieldPendingIds (v1.27.2): suporta clicks concorrentes sem race.
  const [whatsapiPendingIds, setWhatsapiPendingIds] = useState<Set<number>>(
    () => new Set(),
  )

  const whatsapiToggleMutation = useMutation<
    { tenantId: number; enabled: boolean },
    Error,
    { tenantId: number; enabled: boolean },
    { tenantId: number; oldValue: boolean | null } | undefined
  >({
    mutationFn: async ({ tenantId, enabled }) => {
      await whatsapiAdminApi.toggleEnabled(tenantId, enabled)
      return { tenantId, enabled }
    },
    onMutate: async ({ tenantId, enabled }) => {
      setWhatsapiPendingIds((prev) => {
        const next = new Set(prev)
        next.add(tenantId)
        return next
      })
      await qc.cancelQueries({ queryKey: ['admin-tenants'] })
      const current = qc.getQueryData<TenantRow[]>(['admin-tenants'])
      const oldValue = current?.find((t) => t.id === tenantId)?.whatsapi_enabled ?? null
      qc.setQueryData<TenantRow[]>(['admin-tenants'], (old) =>
        old?.map((t) =>
          t.id === tenantId ? { ...t, whatsapi_enabled: enabled } : t,
        ),
      )
      return { tenantId, oldValue }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        // Cirurgico: reverte SO o whatsapi_enabled do tenant especifico
        qc.setQueryData<TenantRow[]>(['admin-tenants'], (current) =>
          current?.map((t) =>
            t.id === ctx.tenantId
              ? { ...t, whatsapi_enabled: ctx.oldValue }
              : t,
          ),
        )
      }
      notifications.show({ title: 'Erro', message: 'Falha ao atualizar configuração WhatsApi', color: 'red' })
    },
    onSuccess: () => {
      notifications.show({ title: 'WhatsApi atualizado', message: 'Integração atualizada com sucesso', color: 'teal' })
    },
    onSettled: (_data, _err, vars) => {
      setWhatsapiPendingIds((prev) => {
        const next = new Set(prev)
        next.delete(vars.tenantId)
        return next
      })
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
  })

  // Set<number> de tenant_ids com timezone in-flight (mesmo padrao do whatsapi).
  const [timezonePendingIds, setTimezonePendingIds] = useState<Set<number>>(
    () => new Set(),
  )

  const timezoneMutation = useMutation<
    void,
    Error,
    { tenantId: number; timezone: string | null },
    { tenantId: number; oldValue: string | null | undefined } | undefined
  >({
    mutationFn: async ({ tenantId, timezone }) => {
      // Backend aceita string vazia como "limpar" (volta a usar default).
      await api.patch(
        `/v1/admin/tenants/${tenantId}`,
        { timezone: timezone ?? '' },
      )
    },
    onMutate: async ({ tenantId, timezone }) => {
      setTimezonePendingIds((prev) => {
        const next = new Set(prev)
        next.add(tenantId)
        return next
      })
      await qc.cancelQueries({ queryKey: ['admin-tenants'] })
      const current = qc.getQueryData<TenantRow[]>(['admin-tenants'])
      const oldValue = current?.find((t) => t.id === tenantId)?.timezone
      qc.setQueryData<TenantRow[]>(['admin-tenants'], (old) =>
        old?.map((t) =>
          t.id === tenantId ? { ...t, timezone } : t,
        ),
      )
      return { tenantId, oldValue }
    },
    onSuccess: () => {
      notifications.show({
        title: 'Fuso atualizado',
        message: 'Timezone do tenant salvo. Aplicado no próximo login dos usuários.',
        color: 'teal',
      })
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        qc.setQueryData<TenantRow[]>(['admin-tenants'], (current) =>
          current?.map((t) =>
            t.id === ctx.tenantId ? { ...t, timezone: ctx.oldValue } : t,
          ),
        )
      }
      notifications.show({
        title: 'Erro',
        message: 'Falha ao atualizar fuso do tenant',
        color: 'red',
      })
    },
    onSettled: (_data, _err, vars) => {
      setTimezonePendingIds((prev) => {
        const next = new Set(prev)
        next.delete(vars.tenantId)
        return next
      })
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
  })

  const [regionsTarget, setRegionsTarget] = useState<TenantRow | null>(null)

  const awsRegionsMutation = useMutation<
    void,
    Error,
    { tenantId: number; regions: string[] }
  >({
    mutationFn: async ({ tenantId, regions }) => {
      await api.patch(
        `/v1/admin/tenants/${tenantId}`,
        { aws_regions: regions },
      )
    },
    onSuccess: () => {
      notifications.show({
        title: 'Regiões atualizadas',
        message: 'Regiões AWS do tenant salvas com sucesso.',
        color: 'teal',
      })
      setRegionsTarget(null)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail ?? 'Falha ao atualizar regiões AWS'
      notifications.show({
        title: 'Erro',
        message: String(msg),
        color: 'red',
      })
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
  })

  const gitlabDisableMutation = useMutation({
    mutationFn: async (tenantId: number) => {
      await gitlabApi.disableConfig(tenantId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
      notifications.show({ title: 'GitLab desativado', message: 'Integração desativada com sucesso', color: 'teal' })
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
      notifications.show({ title: 'Erro', message: 'Falha ao atualizar configuração GitLab', color: 'red' })
    },
  })

  /* ── Handlers ── */
  const handleToggleField = (id: number, field: string, value: boolean) => {
    updateMutation.mutate({ id, field, value })
  }

  const handleToggleZabbix = (tenantId: number, enabled: boolean) => {
    zabbixToggleMutation.mutate({ tenantId, enabled })
  }

  const handleToggleWhatsApi = (tenantId: number, enabled: boolean) => {
    whatsapiToggleMutation.mutate({ tenantId, enabled })
  }

  const handleUpdateTimezone = (tenantId: number, timezone: string | null) => {
    timezoneMutation.mutate({ tenantId, timezone })
  }

  const handleEditRegions = (tenant: TenantRow) => {
    setRegionsTarget(tenant)
  }

  const handleProvisioningFailedClick = (tenant: TenantRow) => {
    setProvisioningErrorTenant(tenant)
    openProvisioningErrorModal()
  }

  const handleToggleGitlab = (tenantId: number, currentlyEnabled: boolean) => {
    if (!currentlyEnabled) {
      setGitlabModalTenantId(tenantId)
      openGitlabModal()
    } else {
      if (window.confirm('Deseja desativar a integração GitLab para este tenant?')) {
        gitlabDisableMutation.mutate(tenantId)
      }
    }
  }

  /* ── Filtering ── */
  const filtered = useMemo(() => {
    if (!tenants) return []

    let list = tenants

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(t => tenantMatchesSearch(t, q))
    }

    // Environment filter
    if (filters.environment && filters.environment.length > 0) {
      const envs = new Set(filters.environment)
      list = list.filter(t => envs.has(t.environment))
    }

    // Status filter
    if (filters.status === 'active') {
      list = list.filter(t => t.is_active)
    } else if (filters.status === 'inactive') {
      list = list.filter(t => !t.is_active)
    }

    // Compliance filter
    if (filters.compliance) {
      const complianceMap: Record<string, (t: TenantRow) => boolean> = {
        terraform: t => t.has_terraform,
        waf: t => t.has_waf,
        pci: t => t.has_pci,
        pentest: t => t.has_pentest,
        soc: t => t.has_soc,
        zabbix: t => !!t.zabbix_enabled,
        gitlab: t => !!t.git_enabled,
        whatsapi: t => !!t.whatsapi_enabled,
      }
      const check = complianceMap[filters.compliance]
      if (check) list = list.filter(check)
    }

    return list
  }, [tenants, searchQuery, filters])

  /* ── KPI computations ── */
  const kpis = useMemo(() => {
    if (!tenants) return { total: 0, active: 0, prd: 0, compliant: 0, complianceRate: '0%' }
    const active = tenants.filter(t => t.is_active).length
    const prd = tenants.filter(t => t.environment === 'prd').length
    const compliant = tenants.filter(t => t.has_terraform && t.has_waf).length
    const rate = tenants.length > 0 ? Math.round((compliant / tenants.length) * 100) : 0
    return { total: tenants.length, active, prd, compliant, complianceRate: `${rate}%` }
  }, [tenants])

  /* ── Error state ── */
  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Erro ao carregar tenants">
        {String(error)}
      </Alert>
    )
  }

  return (
    <Stack gap="md">
      {/* Header com acoes (TASK-2437) */}
      <Group justify="space-between" align="center">
        <Title order={3} c="var(--ai-text-primary)" m={0}>
          Tenants
        </Title>
        <Group gap="xs">
          <OrgSyncButton />
          <Button
            variant="subtle"
            rightSection={<IconArrowRight size={14} />}
            onClick={() => navigate('/admin/tenants/jobs')}
            data-testid="tenants-jobs-link"
          >
            Provisioning Jobs
          </Button>
          <Button
            leftSection={<IconPlus size={14} />}
            onClick={() => navigate('/admin/tenants/new')}
            data-testid="tenants-new-button"
          >
            Novo tenant
          </Button>
        </Group>
      </Group>

      {/* KPI Cards */}
      {isLoading ? (
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={80} radius="md" />)}
        </SimpleGrid>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <KpiCard icon={IconBuilding} color="inovareTeal" value={kpis.total} label="Total de Tenants" />
          <KpiCard icon={IconActivity} color="teal" value={kpis.active} label="Ativos" sub={`${kpis.total - kpis.active} inativos`} />
          <KpiCard icon={IconServer} color="red" value={kpis.prd} label="Produção" sub={`de ${kpis.total} ambientes`} />
          <KpiCard icon={IconShieldCheck} color="violet" value={kpis.complianceRate} label="Compliance" sub={`${kpis.compliant} com Terraform + WAF`} />
        </SimpleGrid>
      )}

      {/* Main Content */}
      <WidgetCard>
        <Stack gap="md">
          <TenantsToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filtersOpen={filtersOpen}
            onToggleFilters={() => setFiltersOpen(o => !o)}
            activeFilterCount={countActiveFilters(filters)}
            totalCount={tenants?.length ?? 0}
            filteredCount={filtered.length}
          />

          <TenantsFilterPanel
            opened={filtersOpen}
            filters={filters}
            onFiltersChange={setFilters}
            onApply={() => setFiltersOpen(false)}
            onClear={() => { setFilters(EMPTY_FILTERS); setFiltersOpen(false) }}
          />

          <TenantsOuTreeView
            tenants={filtered}
            allTenants={tenants ?? []}
            ouNodes={ouTreeData?.nodes ?? []}
            ouTreeError={!!ouTreeError}
            loading={isLoading}
            searchQuery={searchQuery}
            hasActiveFilter={countActiveFilters(filters) > 0}
            onToggleField={handleToggleField}
            onToggleZabbix={handleToggleZabbix}
            onToggleGitlab={handleToggleGitlab}
            onToggleWhatsApi={handleToggleWhatsApi}
            onUpdateTimezone={handleUpdateTimezone}
            onEditRegions={handleEditRegions}
            onProvisioningFailedClick={handleProvisioningFailedClick}
            fieldPendingIds={fieldPendingIds}
            whatsapiPendingIds={whatsapiPendingIds}
            timezonePendingIds={timezonePendingIds}
          />
        </Stack>
      </WidgetCard>

      <GitLabConfigModal
        opened={gitlabModalOpened}
        onClose={closeGitlabModal}
        tenantId={gitlabModalTenantId}
        onSaved={() => qc.invalidateQueries({ queryKey: ['admin-tenants'] })}
      />

      <ProvisioningErrorModal
        opened={provisioningErrorOpened}
        onClose={closeProvisioningErrorModal}
        tenant={provisioningErrorTenant}
      />

      {regionsTarget && (
        <AwsRegionsModal
          key={regionsTarget.id}
          opened={regionsTarget !== null}
          tenantName={regionsTarget.name}
          currentRegions={regionsTarget.aws_regions ?? ['us-east-1']}
          saving={awsRegionsMutation.isPending}
          onClose={() => setRegionsTarget(null)}
          onSave={(regions) =>
            awsRegionsMutation.mutate({ tenantId: regionsTarget.id, regions })
          }
        />
      )}
    </Stack>
  )
}
